import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { EmptyState } from '@/components/EmptyState';
import { api, thumbUrl, mediaUrl } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, MapPin, CheckCircle2, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MasonryGrid } from '@/components/MasonryGrid';
import { MediaLightbox } from '@/components/MediaLightbox';

export default function MemoriesPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState('saved');
  const [past, setPast] = useState([]);
  const [bucket, setBucket] = useState([]);
  const [saved, setSaved] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', location_text: '', image_url: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [evR, bcR, fvR] = await Promise.all([
          api.get('/events?scope=mine'),
          api.get('/bucket-list'),
          api.get('/me/favorites'),
        ]);
        if (!mounted) return;
        setPast((evR.data || []).filter((e) => e.status === 'past'));
        setBucket(bcR.data || []);
        setSaved(fvR.data || []);
      } catch (err) {
        console.error('Failed to load memories:', err);
        toast.error('Failed to load memories');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const addBucket = async () => {
    if (!form.title.trim()) return toast.error('Add a title');
    try {
      const r = await api.post('/bucket-list', form);
      setBucket((arr) => [r.data, ...arr]);
      setOpen(false);
      setForm({ title: '', description: '', location_text: '', image_url: '' });
      toast.success('Added to bucket list');
    } catch (err) { toast.error('Failed to add'); }
  };

  const toggleAchieved = async (b) => {
    try {
      const r = await api.put(`/bucket-list/${b.id}`, { achieved: !b.achieved });
      setBucket((arr) => arr.map((x) => x.id === b.id ? r.data : x));
    } catch (e) { toast.error('Failed'); }
  };

  const removeBucket = async (b) => {
    try {
      await api.delete(`/bucket-list/${b.id}`);
      setBucket((arr) => arr.filter((x) => x.id !== b.id));
      toast.success('Removed');
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div data-testid="memories-page">
      <TopBar title="Memories" subtitle="Stories worth keeping." user={user} />
      <div className="mb-4">
        <SegmentedTabs
          testid="memories-segmented"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'saved', label: 'Saved' },
            { value: 'previous', label: 'Previous' },
            { value: 'bucket', label: 'Bucket' },
          ]}
        />
      </div>
      {tab === 'saved' && (
        loading ? <div className="h-44 rounded-2xl bg-secondary animate-pulse" /> :
        saved.length === 0 ? (
          <EmptyState title="No saved memories yet" description="Tap the heart on any photo or video to save it here for quick access." />
        ) : (
          <>
            <MasonryGrid testid="saved-grid">
              {saved.map((m, idx) => (
                <button key={m.id} onClick={() => setLightboxIdx(idx)} data-testid={`saved-media-${m.id}`} className="block w-full overflow-hidden rounded-xl bg-secondary relative">
                  <div style={{ aspectRatio: `${Math.max(0.5, Math.min(1.8, (m.width && m.height) ? m.width / m.height : 1))}` }}>
                    {m.is_video ? (
                      <video src={mediaUrl(m.id)} preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={thumbUrl(m.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="absolute top-1.5 left-1.5"><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 drop-shadow" /></div>
                </button>
              ))}
            </MasonryGrid>
            <MediaLightbox open={lightboxIdx >= 0} indexInitial={lightboxIdx} items={saved} onClose={() => setLightboxIdx(-1)} onUpdated={(u, i) => setSaved((arr) => u.favorited === false ? arr.filter((x) => x.id !== u.id) : arr.map((x, j) => j === i ? u : x))} />
          </>
        )
      )}
      {tab === 'previous' && (
        loading ? <div className="h-44 rounded-2xl bg-secondary animate-pulse" /> :
        past.length === 0 ? (
          <EmptyState title="No past memoirs yet" description="As your events end, they will appear here as memories." />
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="memories-grid">
            {past.map((p) => (
              <Link key={p.id} to={`/events/${p.id}`} data-testid={`memory-${p.id}`} className="block">
                <Card className="rounded-2xl overflow-hidden">
                  <div className="aspect-[4/3] bg-secondary relative">
                    {p.cover_image ? <img src={p.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 gradient-signature" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2 text-white text-sm font-semibold line-clamp-2">{p.title}</div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )
      )}
      {tab === 'bucket' && (
        <div data-testid="bucket-section">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setOpen(true)} data-testid="bucket-add-button" className="rounded-xl gradient-fab text-white"><Plus className="h-4 w-4 mr-2" />Add to bucket</Button>
          </div>
          {bucket.length === 0 ? (
            <EmptyState title="Dream big" description="Save destinations and dream trips you'd love to capture some day." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="bucket-grid">
              {bucket.map((b) => (
                <motion.div key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="rounded-2xl overflow-hidden" data-testid={`bucket-card-${b.id}`}>
                    <div className="h-32 bg-secondary relative">
                      {b.image_url ? <img src={b.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 gradient-signature" />}
                      {b.achieved && <div className="absolute top-2 right-2 bg-[hsl(var(--success))] text-white px-2 py-0.5 rounded-full text-[10px] font-medium">Achieved</div>}
                    </div>
                    <div className="p-3">
                      <div className="font-semibold text-sm">{b.title}</div>
                      {b.location_text && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{b.location_text}</div>}
                      {b.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.description}</div>}
                      <div className="mt-3 flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => toggleAchieved(b)} data-testid={`bucket-achieve-${b.id}`} className="rounded-xl text-xs"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{b.achieved ? 'Unmark' : 'Achieved'}</Button>
                        <Button variant="ghost" size="sm" onClick={() => removeBucket(b)} data-testid={`bucket-delete-${b.id}`} className="rounded-xl text-xs text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bucket-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-serif">Add to bucket list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="bucket-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Banff in autumn" /></div>
            <div><Label>Location</Label><Input data-testid="bucket-location-input" value={form.location_text} onChange={(e) => setForm((f) => ({ ...f, location_text: e.target.value }))} placeholder="Alberta, Canada" /></div>
            <div><Label>Image URL</Label><Input data-testid="bucket-image-input" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://…" /></div>
            <div><Label>Description</Label><Textarea rows={3} data-testid="bucket-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="bucket-cancel" className="rounded-xl">Cancel</Button>
            <Button onClick={addBucket} data-testid="bucket-save" className="rounded-xl gradient-fab text-white">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
